import { useRef } from "react";

const COFFEE_STYLE = {
    CRITICAL: "text-red-600",
    ACQUIRING: "text-cyan-700 animate-status",
    RESTORED: "text-emerald-600",
};
const COFFEE_LABEL = {
    CRITICAL: "CRITICAL",
    ACQUIRING: "ACQUIRING COFFEE...",
    RESTORED: "COFFEE SYSTEM RESTORED",
};

export default function Hero({ coffee, coffeeReserves = 0, timbotMood, onCoffee, onPoke }) {
    const sectionRef = useRef(null);
    const glowRef = useRef(null);
    const crossXRef = useRef(null);
    const crossYRef = useRef(null);

    const handleMouse = (e) => {
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (glowRef.current) {
            glowRef.current.style.background = `radial-gradient(560px at ${x}px ${y}px, rgba(34,211,238,0.08), transparent 70%)`;
        }
        if (crossXRef.current) crossXRef.current.style.transform = `translateY(${y}px)`;
        if (crossYRef.current) crossYRef.current.style.transform = `translateX(${x}px)`;
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            onMouseMove={handleMouse}
            className="relative min-h-[88vh] flex items-center justify-center tb-grid tb-scanline border-b border-subtle overflow-hidden py-24"
        >
            <div className="absolute inset-0 bg-gradient-to-b from-[#f2f4f6] via-transparent to-[#f2f4f6] pointer-events-none" />
            <div ref={glowRef} className="absolute inset-0 pointer-events-none" />
            <div ref={crossXRef} className="tb-crosshair-x" />
            <div ref={crossYRef} className="tb-crosshair-y" />

            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
                <div className="font-mono text-[11px] text-cyan-600/80 tracking-[0.3em] uppercase mb-6">
                    Independent Experimental Robotics &amp; Software
                </div>

                <h1 data-testid="hero-wordmark" className="animate-wordmark flex justify-center">
                    <img
                        src="/timbotic-wordmark.webp"
                        alt="timbotic.com"
                        width="1024"
                        height="1024"
                        className="w-64 sm:w-80 lg:w-[26rem] h-auto drop-shadow-[0_18px_30px_rgba(6,182,212,0.25)] select-none"
                        draggable="false"
                        data-testid="hero-wordmark-image"
                    />
                </h1>

                <div className="mt-5 font-mono text-xs sm:text-sm text-cyan-600 tracking-[0.35em] uppercase">
                    Build // Break // Fix // Repeat
                </div>

                <p className="mt-8 max-w-2xl mx-auto text-lg sm:text-2xl text-slate-700 font-medium leading-snug">
                    Building things that probably didn&rsquo;t need to exist.
                </p>
                <p className="mt-3 max-w-xl mx-auto text-sm sm:text-base text-slate-500">
                    Robots. Agents. Software. Questionable engineering decisions.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                        href="#timbot"
                        data-testid="cta-enter-lab"
                        className="w-full sm:w-auto px-8 py-4 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono font-bold text-sm tracking-wider uppercase transition-colors shadow-[0_0_25px_rgba(6,182,212,0.4)]"
                    >
                        Enter the Lab
                    </a>
                    <a
                        href="#experiments"
                        data-testid="cta-view-experiments"
                        className="w-full sm:w-auto px-8 py-4 rounded border border-cyan-500/40 bg-white/70 hover:bg-slate-200 text-cyan-800 font-mono text-sm tracking-wider uppercase transition-colors"
                    >
                        View Current Experiments
                    </a>
                </div>

                <div
                    data-testid="hero-status-panel"
                    className="mt-14 max-w-4xl mx-auto p-4 rounded-lg bg-white/85 border border-subtle font-mono text-xs text-left grid grid-cols-2 sm:grid-cols-5 gap-4 text-slate-600"
                >
                    <div>
                        <span className="text-slate-500 block text-[10px]">LAB</span>
                        <span className="text-emerald-600 font-semibold">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-led mr-1.5" />
                            ONLINE
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid="status-timbot"
                        onClick={onPoke}
                        className="text-left focus:outline-none group"
                        title="Do not poke the agent."
                    >
                        <span className="text-slate-500 block text-[10px]">TIMBOT</span>
                        <span className={`font-semibold group-hover:underline ${timbotMood === "ANNOYED" ? "text-amber-600" : "text-cyan-600"}`}>
                            {timbotMood}
                        </span>
                    </button>
                    <div>
                        <span className="text-slate-500 block text-[10px]">ROBOTICS</span>
                        <span className="text-emerald-600 font-semibold">ACTIVE</span>
                    </div>
                    <button
                        type="button"
                        data-testid="status-coffee"
                        onClick={onCoffee}
                        className="text-left focus:outline-none group"
                        title="Initiate coffee acquisition protocol."
                    >
                        <span className="text-slate-500 block text-[10px]">COFFEE</span>
                        <span
                            className={`font-semibold group-hover:underline ${
                                coffee === "CRITICAL" && coffeeReserves > 0
                                    ? "text-emerald-600"
                                    : COFFEE_STYLE[coffee]
                            }`}
                        >
                            {coffee === "CRITICAL" && coffeeReserves > 0
                                ? `STOCKED ×${coffeeReserves}`
                                : COFFEE_LABEL[coffee]}
                        </span>
                    </button>
                    <div>
                        <span className="text-slate-500 block text-[10px]">CURRENT EXPERIMENTS</span>
                        <span className="text-amber-600 font-semibold">TOO MANY</span>
                    </div>
                </div>

                <p className="mt-4 font-mono text-[11px] text-slate-500 italic">
                    &ldquo;No venture capital was harmed in the making of this laboratory.&rdquo;
                </p>
            </div>
        </section>
    );
}
